import React, { useState, useRef, useEffect, useCallback } from "react";

interface AutocompleteInputProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  fetchSuggestions: (prefix: string) => Promise<string[]>;
  placeholder?: string;
  debounceMs?: number;
  className?: string;
}

export const AutocompleteInput: React.FC<AutocompleteInputProps> = ({
  id,
  value,
  onChange,
  fetchSuggestions,
  placeholder,
  debounceMs = 300,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchAndSet = useCallback(async (prefix: string) => {
    if (!prefix.trim()) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    try {
      const results = await fetchSuggestions(prefix);
      setSuggestions(results);
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [fetchSuggestions]);

  function handleInput(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    onChange(val);
    setOpen(true);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchAndSet(val);
    }, debounceMs);
  }

  function handleSelect(suggestion: string) {
    onChange(suggestion);
    setSuggestions([]);
    setOpen(false);
  }

  function handleBlur() {
    setTimeout(() => setOpen(false), 150);
  }

  function handleFocus() {
    if (value.trim()) {
      setOpen(true);
      fetchAndSet(value);
    }
  }

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <div className="relative w-full">
      <input
        id={id}
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInput}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        autoComplete="off"
        className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${className || ""}`}
      />
      {loading && (
        <div className="absolute right-2 top-2 text-xs text-muted-foreground">...</div>
      )}
      {open && suggestions.length > 0 && (
        <ul className="absolute z-10 w-full bg-background border rounded shadow mt-1 max-h-48 overflow-auto">
          {suggestions.map((suggestion) => (
            <li key={suggestion}>
              <button
                type="button"
                className="w-full text-left px-2 py-1 hover:bg-muted text-sm"
                onMouseDown={() => handleSelect(suggestion)}
              >
                {suggestion}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
